import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import {
  YouTube as TwitchIcon,
  CalendarToday as CalendarIcon,
  LiveTv as LiveIcon,
} from '@mui/icons-material';

const QVGDPLOIOProject: React.FC = () => {
  return (
    <Box>
      {/* Header avec titre et image */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <img
          src="/Twitch/QVGDPLOIO.png"
          alt="Logo Qui veut gagner des places pour Red Bull League Of Its Own"
          style={{
            width: '64px',
            height: '64px',
            objectFit: 'contain',
            filter: 'drop-shadow(2px 2px 4px rgba(167, 139, 250, 0.3))',
          }}
        />
        <Typography
          variant="h4"
          component="h2"
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Qui veut gagner des places pour Red Bull League Of Its Own
        </Typography>
      </Box>

      {/* Statut et date */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            2025
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{ mb: 3, lineHeight: 1.6 }}
      >
        Émission interactive développée pour l'évènement Red Bull League Of Its Own. 
        Format quiz inspiré des grands jeux télévisés où des duos de streamers et joueurs 
        professionnels de League of Legends répondent à des questions pour faire gagner 
        des places et cadeaux aux viewers. L'émission a été diffusée en direct avec une 
        interface de régie complètement automatisée.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Fonctionnalités principales */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Fonctionnalités principales
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
          gap: 2 
        }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Système de quiz avec paliers de gains
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Développement de mécanique de jokers
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Sélection automatique des gagnants
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Interface de régie streamers
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Intégration Twitch API complète
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Gestion de la participation du chat
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Overlay temps réel pour le stream
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Technologies */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Technologies utilisées
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {['TypeScript', 'React', 'Express.js', 'Twitch API', 'Socket.IO', 'Node.js', 'OBS Integration'].map((tech) => (
            <Chip
              key={tech}
              label={tech}
              variant="filled"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(20, 184, 166, 0.1) 100%)',
                color: '#a78bfa',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2) 0%, rgba(20, 184, 166, 0.2) 100%)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Défis techniques */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Défis techniques relevés
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Gestion temps réel :</strong> Synchronisation parfaite entre l'interface de régie, 
          les overlays stream et le système de sélection des participants via l'API Twitch.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Automatisation complète :</strong> Interface intuitive permettant aux streamers 
          de gérer l'émission sans assistance technique, avec gestion automatique des transitions 
          et animations.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Scalabilité :</strong> Système capable de gérer plusieurs milliers de participants 
          simultanés avec calcul en temps réel de l'engagement basé sur l'activité dans le chat.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Liens actions */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<TwitchIcon />}
          href="https://www.youtube.com/watch?v=5L8_uSxYPMs"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #8b5cf6 0%, #0f766e 100%)',
            },
          }}
        >
          Voir l'émission
        </Button>
      </Box>
    </Box>
  );
};

export default QVGDPLOIOProject;




